import { DisplayMarketAsset, getPointsLabel, LINKS } from "@silo/types";
import { YieldMetrics } from "./utils";
import { cn } from "../../utils/class-names";
import { BreakdownCollateral } from "../apr-display-v2/breakdown/breakdown-collateral";
import { BreakdownProtected } from "../apr-display-v2/breakdown/breakdown-protected";
import { ProtectedRewardsMessage } from "../apr-display-v2";
import { Perk } from "../perk";
import { ExternalLink } from "../link";
import { PointsLinks } from "../perk/points-links";
import { formatDepositApr } from "../apr-display-v2/utils";

interface DepositAprContentProps {
  asset: DisplayMarketAsset;
  yieldMetrics: YieldMetrics;
}

export function DepositAprContent({
  asset,
  yieldMetrics,
}: DepositAprContentProps): JSX.Element {
  const { symbol, collateralBaseApr } = asset;
  const {
    displayCollateralYields,
    collateralRewards,
    hasCollateralRewards,
    hasProtectedRewards,
    protectedRewards,
    depositPoints,
    depositMerklRewards,
    hasProtectedIncentives,
    hasDepositIncentives,
  } = yieldMetrics;

  const showCollateralBreakdown =
    displayCollateralYields && hasCollateralRewards;

  const showProtectedBreakdown =
    !displayCollateralYields && hasProtectedIncentives;

  const hasDepositSuperFest = depositMerklRewards.some(
    (reward) => reward.isSuperFest
  );

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-2.5">
        <div className="font-medium text-white">{symbol} total deposit APR</div>

        <div
          className={cn(
            "font-mono text-2xl font-medium",
            hasDepositIncentives ? "text-amber-300" : "text-white"
          )}
        >
          {hasDepositIncentives && "+"}
          {formatDepositApr(yieldMetrics)}
        </div>
      </div>

      {showCollateralBreakdown && (
        <div className="flex flex-col gap-5">
          <BreakdownCollateral
            collateralRewards={collateralRewards}
            collateralBaseApr={collateralBaseApr}
            align="left"
          />

          {hasCollateralRewards && (
            <ExternalLink size="small" href={LINKS.SILOPEDIA.INCENTIVES}>
              Rewards details
            </ExternalLink>
          )}

          {hasDepositSuperFest && (
            <ExternalLink size="small" href={LINKS.SUPERFEST}>
              SuperFest details
            </ExternalLink>
          )}
        </div>
      )}

      {showProtectedBreakdown && (
        <div className="flex flex-col gap-5">
          <BreakdownProtected
            protectedRewards={protectedRewards}
            align="left"
          />

          {hasProtectedRewards && (
            <>
              <ProtectedRewardsMessage protectedRewards={protectedRewards} />

              <ExternalLink size="small" href={LINKS.SILOPEDIA.INCENTIVES}>
                Rewards details
              </ExternalLink>
            </>
          )}

          {hasDepositSuperFest && (
            <ExternalLink size="small" href={LINKS.SUPERFEST}>
              SuperFest details
            </ExternalLink>
          )}
        </div>
      )}


      {depositPoints.length > 0 && (
        <div className="flex flex-col gap-5">
          <Perk.Root>
            <Perk.Label />

            {depositPoints.map((pointsItem) => (
              <Perk.Item>
                <Perk.PointsIcon
                  pointsKey={pointsItem._tag}
                  className={Perk.iconClassName}
                />

                <Perk.Emphasis>{getPointsLabel(pointsItem)}</Perk.Emphasis>

                <Perk.PointsLabel />
              </Perk.Item>
            ))}
          </Perk.Root>

          <PointsLinks pointsItems={depositPoints} />
        </div>
      )}
    </div>
  );
}
